import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Trophy, HandHeart, Eye, Camera, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const stats = [
  { icon: HandHeart, value: "140.000+", label: "Euro Spenden", color: "#b46eb6" },
  { icon: Users, value: "1.814", label: "Läufer:innen", color: "#7DD4B5" },
  { icon: Eye, value: "2.000+", label: "Besucher:innen", color: "#b46eb6" },
  { icon: Trophy, value: "130", label: "Helfer:innen", color: "#7DD4B5" },
];

const TOKEN_2026 = "eyJhbGciOiJIUzI1NiJ9.eyJrZXkiOiI2YTA5ZDViZjk3Yzg3YjAwMDJlZTg4MzMvU0o3SXl2S2haemUiLCJleHAiOjE4MjEzMDQ4MDB9.cKA9kLvzKDyiECo8dVLNKb5HvK8m5OUI3hVYozVfnJI";
const BASE_2026 = "https://images-prod-eu.photostore24.online/6a09d5bf97c87b0002ee8833/SJ7IyvKhZze";

const galleryPreview2025 = [
  "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/ox2v9tmy_f3d72cb2-221f-4ff3-932c-15ca13f80ea8.JPG",
  "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/sbxk3wmc_3db4034f-c98f-496f-9cc7-389fd14f5448.JPG",
  "https://customer-assets.emergentagent.com/job_65cff9e4-1fb7-4087-8c22-d5f62489d89d/artifacts/tm2qx58w_IMG_8213.jpg",
];

const galleryPreview2026 = [
  `${BASE_2026}/ByqlwF3Wzg/M.jpg?token=${TOKEN_2026}`,
  `${BASE_2026}/B19ePKnZMg/M.jpg?token=${TOKEN_2026}`,
  `${BASE_2026}/Sy9evY2ZGl/M.jpg?token=${TOKEN_2026}`,
];

export default function EventInfo() {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.15 });
  const [leftRef, leftVisible] = useScrollAnimation({ threshold: 0.1 });
  const [rightRef, rightVisible] = useScrollAnimation({ threshold: 0.1 });

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="event" data-testid="event-section" className="py-20 md:py-28" style={{ background: "#F8F9FA" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 animate-fade-up ${headerVisible ? 'visible' : ''}`}
        >
          <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>
            Das Event
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
            Run for Rett – Gemeinsam laufen, gemeinsam helfen
          </h2>
          <div className="section-divider mx-auto mt-4" />
        </div>

        {/* Stats Grid */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16">
          {stats.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={idx} 
                data-testid={`stat-card-${idx}`} 
                className={`stat-card border-0 shadow-md bg-white text-center card-animate ${statsVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <CardContent className="p-6 md:p-8">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(149, 225, 187, 0.5)" }}>
                    <Icon className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-extrabold" style={{ color: stat.color }}>
                    {stat.value}
                  </p>
                  <p className="text-sm mt-1" style={{ color: "#9CA3AF" }}>{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Event info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left side - Text content */}
          <div 
            ref={leftRef}
            className={`space-y-5 text-left pt-6 animate-fade-left ${leftVisible ? 'visible' : ''}`}
          >
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              In <strong>2025</strong> und <strong>2026</strong> fanden in Herrnried zwei große Spendenläufe statt.
              Dank der großartigen Unterstützung von über <strong>1.814 Läufer:innen</strong>,{" "}
              <strong>130 Helfer:innen</strong>, mehr als <strong>2.000 Besucher:innen</strong>{" "}
              und vielen großzügigen <strong>Spender:innen</strong> konnten über{" "}
              <strong style={{ color: "#b46eb6" }}>140.000 € gesammelt</strong> werden.
            </p>
            <p className="text-base md:text-lg leading-relaxed pb-4" style={{ color: "#4B5563" }}>
              Diese Spenden wurden an den Verein{" "}
              <strong style={{ color: "#b46eb6" }}>Rett Syndrom Research Trust (RSRT)</strong>{" "}
              weitergegeben, der Forschungsprojekte unterstützt.
            </p>

            {/* Separator line */}
            <div className="border-t border-gray-200 my-6"></div>

            <div className="pt-8">
              <h3 className="font-heading text-xl font-bold mb-3" style={{ color: "#1F2937" }}>
                Was euch bei unseren Events erwartet
              </h3>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "#4B5563" }}>
                Neben dem Spendenlauf erwartet euch ein buntes Rahmenprogramm für die ganze Familie:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Bühne mit musikalischen Live-Acts",
                  "Tombola mit tollen Preisen",
                  "ein buntes Programm für Kinder",
                  "Kaffee & Kuchen",
                  "Erfrischende Getränke",
                  "Grillspezialitäten",
                  "Essen vom Gasthaus Neugebauer",
                  "Verschiedene Live Acts auf dem Weg",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ background: "#b46eb6" }} />
                    <span className="text-base font-medium" style={{ color: "#4B5563" }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right side - Gallery Preview + Next Event Card */}
          <div 
            ref={rightRef}
            className={`space-y-6 animate-fade-right ${rightVisible ? 'visible' : ''}`}
          >
            {/* Gallery Previews 2025 + 2026 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-bold flex items-center gap-2" style={{ color: "#1F2937" }}>
                  <Camera className="w-4 h-4" style={{ color: "#b46eb6" }} />
                  Impressionen 2025
                </h3>
                <Link to="/spendenlauf#galerie" className="flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: "#b46eb6" }}>
                  Alle Bilder <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2.5 mb-5">
                {galleryPreview2025.map((src, i) => (
                  <Link key={i} to="/spendenlauf#galerie" className="block">
                    <div className="aspect-square rounded-lg overflow-hidden group cursor-pointer shadow-sm">
                      <img src={src} alt={`2025 - ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                    </div>
                  </Link>
                ))}
              </div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-base font-bold flex items-center gap-2" style={{ color: "#1F2937" }}>
                  <Camera className="w-4 h-4" style={{ color: "#7DD4B5" }} />
                  Impressionen 2026
                </h3>
                <Link to="/spendenlauf#galerie-2026" className="flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: "#7DD4B5" }}>
                  Alle Bilder <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-3 gap-2.5">
                {galleryPreview2026.map((src, i) => (
                  <Link key={i} to="/spendenlauf#galerie-2026" className="block">
                    <div className="aspect-square rounded-lg overflow-hidden group cursor-pointer shadow-sm">
                      <img src={src} alt={`2026 - ${i + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" loading="lazy" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Info card */}
            <Card data-testid="next-event-card" className="border-0 shadow-xl overflow-hidden">
              <div className="h-2 w-full" style={{ background: "linear-gradient(90deg, #b46eb6, #7DD4B5)" }} />
              <CardContent className="p-6 md:p-8 space-y-4">
                <h3 className="font-heading text-2xl font-bold" style={{ color: "#b46eb6" }}>
                  Wir kommen wieder!
                </h3>
                <p className="text-base leading-relaxed" style={{ color: "#4B5563" }}>
                  2027 machen wir eine Pause – aber <strong>2028 greifen wir wieder an!</strong> Bleibt gespannt und folgt uns auf Instagram für Updates.
                </p>
                <Link to="/spendenlauf#galerie" className="block">
                  <Button
                    data-testid="event-gallery-btn"
                    variant="outline"
                    className="w-full rounded-full py-6 text-base font-semibold border-2 border-[#b46eb6]/20 hover:border-[#b46eb6] text-[#b46eb6] transition-all"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Bilder ansehen
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
